import { useCallback, useState } from 'react';
import { useMapInstance } from 'shared/map-exports';
import { MapboxFeature } from 'features/mapping/mapping.types';
import {
  REGIONS_QUERY_LAYER_ID,
  RegionFeatureProperties,
} from 'features/mapping/components/layers/RegionsLayer/RegionsLayer';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';

/**
 * De-dupes by feature id: geometries can be split across tile boundaries, so
 * the same region can be returned more than once.
 */
const dedupeById = (
  features: MapboxFeature<RegionFeatureProperties>[],
): MapboxFeature<RegionFeatureProperties>[] => {
  const seen = new Set<string | number>();

  return features.filter((feature) => {
    if (feature.id == null) return false;
    if (seen.has(feature.id)) return false;
    seen.add(feature.id);
    return true;
  });
};

/**
 * Regions currently rendered in the map viewport, updating as the user
 * pans/zooms. Backed by the invisible RegionsLayer fill layer.
 */
export const useVisibleRegions = (
  enabled: boolean,
): RegionFeatureProperties[] => {
  const map = useMapInstance();
  const [regions, setRegions] = useState<RegionFeatureProperties[]>([]);

  const updateVisibleRegions = useCallback((): void => {
    if (!map?.getLayer(REGIONS_QUERY_LAYER_ID)) {
      setRegions([]);
      return;
    }

    const features = map.queryRenderedFeatures({
      layers: [REGIONS_QUERY_LAYER_ID],
    }) as unknown as MapboxFeature<RegionFeatureProperties>[];

    setRegions(dedupeById(features).map((feature) => feature.properties));
  }, [map]);

  useMapEvent(['moveend', 'load'], updateVisibleRegions, map, enabled);

  return regions;
};
